/**
 * Welke resolutie is nog aanvaardbaar voor een drukbestand.
 *
 * Hoe groter het formaat, hoe verder de kijkafstand, en hoe minder dpi er nodig
 * is. Een visitekaartje houd je in je hand, een spandoek zie je van tien meter.
 * Dezelfde ladder voedt zowel de groene of oranje melding die de klant in de
 * ontwerpmodule ziet, als de harde grens waaronder er niets meer de deur uit
 * gaat.
 */

export const ACCEPTABLE_DPI_BY_SIZE: { maxCm: number; dpi: number }[] = [
  { maxCm: 30, dpi: 150 },
  { maxCm: 100, dpi: 100 },
  { maxCm: 200, dpi: 72 },
  { maxCm: Number.POSITIVE_INFINITY, dpi: 50 },
];

export function acceptableDpiForSize(largestCm: number) {
  // Onbekend formaat: de strengste trede. Een overbodige waarschuwing kost een
  // klant hooguit een blik; een gemiste waarschuwing kost hem een wazige druk.
  if (!Number.isFinite(largestCm) || largestCm <= 0) return ACCEPTABLE_DPI_BY_SIZE[0].dpi;
  const tier = ACCEPTABLE_DPI_BY_SIZE.find((step) => largestCm <= step.maxCm);
  return tier ? tier.dpi : ACCEPTABLE_DPI_BY_SIZE[0].dpi;
}

/**
 * Hoe ver de AI-vergroting maximaal gaat.
 *
 * `prunaai/p-image-upscale` draait met `upscale_mode: 'factor'` en kan per
 * zijde 1 tot 8 keer vergroten; de uitvoer wordt afgekapt op 128 MP. Op
 * productie gemeten: 1000 x 1000 werd 2000 x 2000 bij factor 2. Sinds
 * 18 oktober 2026 kiezen we de factor per foto: precies wat nodig is om de
 * minimale dpi te halen (soms is 1,1 genoeg, soms 8).
 */
export const AI_UPSCALE_MAX_FACTOR = 8;
/** Kleine marge boven de eis, zodat afronding de melding niet laat staan. */
const UPSCALE_MARGIN = 1.05;

/** De factor (1,1 tot 8, op een tiende naar boven) die de eis net haalt. */
export function upscaleFactorFor(measuredDpi: number, requiredDpi: number): number {
  const needed = (requiredDpi * UPSCALE_MARGIN) / measuredDpi;
  if (!Number.isFinite(needed) || needed <= 0) return AI_UPSCALE_MAX_FACTOR;
  return Math.min(AI_UPSCALE_MAX_FACTOR, Math.max(1.1, Math.ceil(needed * 10) / 10));
}

export type DpiRepair =
  /** De AI-vergroting haalt het in een keer, met deze factor. */
  | { kind: 'upscale'; factor: number; resultingDpi: number }
  /** Alleen kleiner maken haalt het; `scale` is de factor op het huidige formaat. */
  | { kind: 'shrink'; scale: number; resultingDpi: number }
  | null;

/**
 * Welke reparatie deze afbeelding boven de drempel brengt.
 *
 * Waarom dit hier staat en niet als knop in de editor: een knop die draait,
 * slaagt en de waarschuwing laat staan is erger dan geen knop. De vergroting
 * verdubbelt alleen de pixels; het formaat op papier blijft gelijk, dus de dpi
 * verdubbelt en niet meer dan dat. Bij 30 dpi tegen een eis van 100 komt hij
 * uit op 60 en blijft de melding staan. Dan is kleiner maken de enige
 * reparatie die het wel haalt.
 */
export function repairForLowDpi(
  measuredDpi: unknown,
  requiredDpi: number,
  maxFactor: number = AI_UPSCALE_MAX_FACTOR,
): DpiRepair {
  const measured = Number(measuredDpi);
  if (!Number.isFinite(measured) || measured <= 0) return null;
  if (!Number.isFinite(requiredDpi) || requiredDpi <= 0) return null;
  if (measured >= requiredDpi) return null;

  if (measured * maxFactor >= requiredDpi) {
    const factor = Math.min(maxFactor, upscaleFactorFor(measured, requiredDpi));
    return { kind: 'upscale', factor, resultingDpi: measured * factor };
  }

  // Kleiner maken werkt altijd: dezelfde pixels over minder papier. De factor
  // is de verhouding tussen wat we hebben en wat we nodig hebben, met twee
  // procent extra. Precies op de drempel uitkomen is vragen om een meting die
  // door afrondingsverschillen op 99,999 blijft steken, en dan staat de
  // waarschuwing er na de reparatie nog steeds.
  const doelDpi = requiredDpi * 1.02;
  return { kind: 'shrink', scale: measured / doelDpi, resultingDpi: doelDpi };
}

export type PrintRasterVerdict = {
  requiredDpi: number;
  /** Onder de drempel: dit bestand mag niet naar de drukker. */
  blocked: boolean;
};

/**
 * Mag dit raster als drukbestand de deur uit.
 *
 * Een onbekende of onbruikbare dpi blokkeert niet: dan is er niets gemeten en
 * zou blokkeren op een aanname neerkomen. Waar wel gemeten is, telt de meting.
 */
export function judgePrintRaster(
  dpi: unknown,
  pageWidthMm: number | null | undefined,
  pageHeightMm: number | null | undefined,
): PrintRasterVerdict {
  const largestSideCm = Number.isFinite(Number(pageWidthMm)) && Number.isFinite(Number(pageHeightMm))
    ? Math.max(Number(pageWidthMm), Number(pageHeightMm)) / 10
    : 0;
  const requiredDpi = acceptableDpiForSize(largestSideCm);
  const measured = Number(dpi);

  if (!Number.isFinite(measured) || measured <= 0) {
    return { requiredDpi, blocked: false };
  }

  return { requiredDpi, blocked: measured < requiredDpi };
}
